import React from 'react';

import { Add as AddIcon, BorderColor as BorderColorIcon } from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router';

import { parseNullableValue } from '@hrlogics/helper-utils';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';

import type { EmployerTableItemFragment } from 'src/api';
import { ClickableLabel } from 'src/components/common';
import type { TableColumn, TableFilter, TableSelectedRowId } from 'src/components/table';
import { Table, TableExcelColumn, TableRowAction, TableTopAction } from 'src/components/table';
import type { ModalId } from 'src/constants/modals';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal } from 'src/hooks/shared';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { getAddressString } from 'src/utils/address';
import { buildEmployerDetailsUrl } from 'src/utils/employer';

type EmployersInnerTablePEOStaffProps = {
  employersList: EmployerTableItemFragment[];
  employersCount: number;
  loading: boolean;
  inviteModalId?: ModalId;
  pagination: PaginationContextValue;
  onRefresh?: () => Promise<void>;
};

export const EmployersInnerTablePEOStaff: React.FC<EmployersInnerTablePEOStaffProps> = ({
  employersList,
  employersCount,
  loading,
  inviteModalId,
  onRefresh,
  pagination,
}) => {
  const navigate = useNavigate();

  const { openModal } = useModal();
  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const [selectedIds, setSelectedIds] = React.useState<TableSelectedRowId[]>([]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(
    (): Array<TableRowAction<EmployerTableItemFragment>> => [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: employer => {
          navigate(buildEmployerDetailsUrl(employer.id));
        },
      },
    ],
    [navigate],
  );

  const topActions = React.useMemo(
    (): Array<TableTopAction> =>
      inviteModalId
        ? [
            {
              actionName: 'Add',
              show: true,
              icon: <AddIcon />,
              onAction: () => openModal(inviteModalId),
              render() {
                return (
                  <Button
                    key={this.actionName}
                    variant="contained"
                    startIcon={this.icon}
                    onClick={this.onAction}
                  >
                    New
                  </Button>
                );
              },
            },
          ]
        : [],
    [inviteModalId, openModal],
  );

  return (
    <Table
      dataSource={employersList}
      rowCount={employersCount}
      columns={columns}
      rowActions={rowActions}
      topActions={topActions}
      onSearch={setSearchValue}
      pagination={pagination}
      filters={filters}
      selectedIds={selectedIds}
      setSelectedIds={setSelectedIds}
      excelColumns={excelColumns}
      loading={loading}
      showDownloadIcon={false}
      onRefresh={onRefresh}
      onSort={setSortOption}
    />
  );
};

const columns: Array<TableColumn<EmployerTableItemFragment>> = [
  {
    title: 'Name',
    key: 'displayName',
    sortable: true,
    render: (_valueByDataPath, employer) => {
      return (
        <ClickableLabel
          type="link"
          to={buildEmployerDetailsUrl(employer.id)}
          label={employer.displayName}
          logoUrl={employer.logo?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Address',
    key: 'headquarters',
    render: (_valueByDataPath, employer) => {
      return parseNullableValue(getAddressString(employer.headquarters));
    },
  },
];

const excelColumns: Array<TableExcelColumn<EmployerTableItemFragment>> = [
  {
    header: 'Name',
    key: 'displayName',
  },
  {
    header: 'Address',
    key: 'headquarters',
    render: (_valueByKey, employer) => {
      return parseNullableValue(getAddressString(employer.headquarters));
    },
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
